import { Controller } from "@hotwired/stimulus"
import { reactor } from "../lib/reactor"

export default class extends Controller {
  static targets = ["dialog", "input", "list", "failure", "group", "application", "row", "header", "add", "empty", "hint"]
  static values = { url: String }

  connect() {
    reactor(this)
    if (!this.hasDialogTarget) return

    this.onShortcut ||= (event) => this.shortcut(event)
    document.addEventListener("keydown", this.onShortcut, true)
    this.stampPlatform()
    this.syncHint(this.hasInputTarget && this.inputTarget.value.trim() !== "")
  }

  disconnect() {
    if (this.onShortcut) document.removeEventListener("keydown", this.onShortcut, true)
  }

  open() {
    if (!this.hasDialogTarget) {
      const dialog = document.getElementById("next-context-picker")
      const host = dialog && this.application.getControllerForElementAndIdentifier(dialog, "context-picker")
      host?.open()
      return
    }

    if (this.dialogTarget.open) return

    this.dialogTarget.showModal()
    this.resetFilter()
    this.inputTarget.focus()
    this.load()
  }

  async load() {
    const list = this.listTarget
    if (list.contains(document.activeElement)) this.inputTarget.focus()
    list.replaceChildren()
    if (list.src) list.reload()
    else list.src = this.urlValue

    this.loading = true
    await list.loaded
    this.loading = false
    if (!list.hasChildNodes()) list.append(this.failureTarget.content.cloneNode(true))
  }

  // Turbo would otherwise write "Content missing" into the frame.
  missing(event) {
    event.preventDefault()
  }

  // turbo:frame-render fires a repaint after the render, which shows the rows unfiltered.
  render(event) {
    const { render } = event.detail
    event.detail.render = (...frames) => {
      const focused = this.listTarget.contains(document.activeElement)
      render(...frames)
      this.filter()
      if (focused && !this.listTarget.contains(document.activeElement)) this.inputTarget.focus()
    }
  }

  // The close event is queued. On turbo:before-cache the controller disconnects before it fires.
  close() {
    this.dialogTarget.close()
    this.closed()
  }

  // Turbo reloads a refresh="morph" frame that has a src on every page morph.
  closed() {
    this.loading = false
    this.listTarget.removeAttribute("src")
  }

  backdropClose(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  morph() {
    this.stampPlatform()
    if (this.hasDialogTarget) {
      if (!this.dialogTarget.open) this.closed()
      else if (!this.loading) this.listTarget.reload()
    }
    this.syncHint(this.hasInputTarget && this.inputTarget.value.trim() !== "")
  }

  shortcut(event) {
    if (!this.matchesShortcut(event)) return
    const other = document.querySelector("dialog[open]")
    if (other && other !== this.dialogTarget) return

    event.preventDefault()
    if (this.dialogTarget.open) this.close()
    else this.open()
  }

  filter() {
    const query = this.inputTarget.value.trim().toLowerCase()
    const terms = query.split(/\s+/).filter(Boolean)
    const matches = (row) => {
      const haystack = (row.getAttribute("data-filter-text") || "").toLowerCase()
      return terms.every((term) => haystack.includes(term))
    }
    this.syncHint(query !== "")
    let anyVisible = false

    this.groupTargets.forEach((group) => {
      let groupVisible = terms.length === 0

      group.querySelectorAll("[data-context-picker-target='application']").forEach((application) => {
        const [applicationRow, ...managerRows] = application.querySelectorAll("[data-context-picker-target='row']")
        let applicationVisible = matches(applicationRow)

        managerRows.forEach((row) => {
          const match = matches(row)
          row.classList.toggle("hidden", !match)
          if (match) applicationVisible = true
        })

        application.classList.toggle("hidden", !applicationVisible)
        if (applicationVisible) groupVisible = true
      })

      const header = group.querySelector("[data-context-picker-target='header']")
      const add = group.querySelector("[data-context-picker-target='add']")
      header?.classList.toggle("hidden", !groupVisible)
      add?.classList.toggle("hidden", !groupVisible)
      if (groupVisible) anyVisible = true
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", terms.length === 0 || anyVisible || !this.hasGroupTarget)
    }
  }

  resetFilter() {
    if (this.hasInputTarget) this.inputTarget.value = ""
    this.filter()
  }

  matchesShortcut(event) {
    if (event.repeat || event.isComposing || event.altKey || event.shiftKey) return false
    if (event.defaultPrevented) return false
    if (event.key !== "k" && event.key !== "K") return false
    if (this.applePlatform()) return event.metaKey && !event.ctrlKey
    return event.ctrlKey && !event.metaKey
  }

  applePlatform() {
    const platform = navigator.userAgentData && navigator.userAgentData.platform
    if (platform) return platform === "macOS"
    return /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || "")
  }

  stampPlatform() {
    document.documentElement.dataset.platform = this.applePlatform() ? "apple" : "other"
  }

  syncHint(hide) {
    const hint = this.element.querySelector("[data-context-picker-hint]")
    if (hint) hint.hidden = hide
  }
}
